import { createSelector, createSlice, isFulfilled, PayloadAction } from "@reduxjs/toolkit";
import { AffiliateNetwork } from "./api-interfaces";
import { RootState } from "./store";
import { api } from "./api";

export type Platform = 'voluum' | 'redtrack';
export type PlatformFilter = Platform | 'both';
export type PlatformHealth = 'online' | 'offline' | 'degraded';
export type MigrationStatus = 'pending' | 'in_progress' | 'completed' | 'failed';

interface MigrationStatusInfo {
  overall: number; // 0-100%
  campaigns: number;
  offers: number;
  publishers: number;
}

interface PlatformState {
  selectedPlatform: PlatformFilter;
  platformHealth: {
    voluum: PlatformHealth;
    redtrack: PlatformHealth;
  };
  migrationStatus: MigrationStatusInfo;
  affiliateNetworkList: {
    voluum: AffiliateNetwork[];
    redtrack: AffiliateNetwork[];
    unified: AffiliateNetwork[];
  };
  // Legacy support for existing components
  legacyAffiliateNetworkList?: AffiliateNetwork[];
}

const initialState: PlatformState = {
  selectedPlatform: 'both',
  platformHealth: {
    voluum: 'online',
    redtrack: 'online',
  },
  migrationStatus: {
    overall: 0,
    campaigns: 0,
    offers: 0,
    publishers: 0,
  },
  affiliateNetworkList: {
    voluum: [],
    redtrack: [],
    unified: [],
  },
  legacyAffiliateNetworkList: [],
};

const platformSlice = createSlice({
    name: 'platform',
    initialState,
    reducers: {
        setPlatformFilter: (state, action: PayloadAction<PlatformFilter>) => {
            state.selectedPlatform = action.payload;
        },
        setPlatformHealth: (state, action: PayloadAction<{ platform: Platform; status: PlatformHealth }>) => {
            state.platformHealth[action.payload.platform] = action.payload.status;
        },
        setMigrationStatus: (state, action: PayloadAction<Partial<MigrationStatusInfo>>) => {
            state.migrationStatus = { ...state.migrationStatus, ...action.payload };
        },
        setAffiliateNetworks: (state, action: PayloadAction<{ platform: Platform | 'unified'; networks: AffiliateNetwork[] }>) => {
            state.affiliateNetworkList[action.payload.platform] = action.payload.networks;
            
            // Update unified list when both platforms are updated
            if (action.payload.platform !== 'unified') {
                state.affiliateNetworkList.unified = [
                    ...state.affiliateNetworkList.voluum,
                    ...state.affiliateNetworkList.redtrack
                ];
            }
            
            // Legacy support - use unified list
            state.legacyAffiliateNetworkList = state.affiliateNetworkList.unified;
        },
    },
    extraReducers: (builder) => {
        builder
            .addMatcher(
                api.endpoints.sendAction.matchFulfilled,
                (state, action) => {
                    if (action.meta.arg.originalArgs.cmd === 'getAffiliateNetworkList') {
                        // Legacy support - store in both legacy and new structure
                        state.legacyAffiliateNetworkList = action.payload;
                        state.affiliateNetworkList.unified = action.payload;
                    }
                }
            );
    },
});

export const { setPlatformFilter, setPlatformHealth, setMigrationStatus, setAffiliateNetworks } = platformSlice.actions;

// Selectors
export const selectedPlatformSelector = createSelector(
    (state: RootState) => state.platform.selectedPlatform,
    (selectedPlatform) => selectedPlatform
);

export const platformHealthSelector = createSelector(
    (state: RootState) => state.platform.platformHealth,
    (platformHealth) => platformHealth
);

export const migrationStatusSelector = createSelector(
    (state: RootState) => state.platform.migrationStatus,
    (migrationStatus) => migrationStatus
);

export const affiliateNetworkListSelector = createSelector(
    (state: RootState) => state.platform.affiliateNetworkList,
    (state: RootState) => state.platform.selectedPlatform,
    (affiliateNetworkList, selectedPlatform) => {
        switch (selectedPlatform) {
            case 'voluum':
                return affiliateNetworkList.voluum;
            case 'redtrack':
                return affiliateNetworkList.redtrack;
            case 'both':
            default:
                return affiliateNetworkList.unified;
        }
    }
);

// Legacy selector for backward compatibility
export const legacyAffiliateNetworkListSelector = createSelector(
    (state: RootState) => state.platform.legacyAffiliateNetworkList,
    (affiliateNetworkList) => affiliateNetworkList ?? []
);

export default platformSlice.reducer;
